import { useContext, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, MessageCircle, Tent, X } from "lucide-react";
import { LangContext, whatsappUrl } from "../i18n";

export const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, className = "", ...rest }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.8, delay, ease: EASE }} {...rest}>
    {children}
  </motion.div>
);

export function SectionHeading({ data, dark = false }) {
  return (
    <Reveal className={`section-heading ${dark ? "on-dark" : ""}`}>
      <p className="eyebrow">{data.kicker}</p>
      <h2>{data.title}</h2>
      {data.intro && <p className="section-intro">{data.intro}</p>}
      {data.sub && <p className="section-intro">{data.sub}</p>}
    </Reveal>
  );
}

export function WhatsAppButton({ children, onClick, outline = false, testid }) {
  return (
    <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }} data-testid={testid} className={`button ${outline ? "button-outline" : ""}`} onClick={onClick}>
      <MessageCircle size={17} />
      {children}
      <ArrowUpRight size={16} />
    </motion.button>
  );
}

const MARQUEE = ["तीर्थ यात्रा", "Pilgrimage", "सेवा", "Seva", "साधना", "Sadhana", "महाकुंभ", "Maha Kumbh", "चारधाम", "Char Dham", "ज्योतिर्लिंग", "Jyotirlinga"];

export function Marquee() {
  return (
    <div className="marquee" aria-hidden="true" data-testid="editorial-marquee">
      <div className="marquee-track">
        {[...MARQUEE, ...MARQUEE].map((word, i) => (
          <span key={i} className="marquee-word">{word}<i>✦</i></span>
        ))}
      </div>
    </div>
  );
}

export function useWhatsApp() {
  const { copy } = useContext(LangContext);
  return (key) => window.open(whatsappUrl(copy.messages[key] || copy.messages.general), "_blank", "noopener,noreferrer");
}

export function Header() {
  const { lang, copy, changeLang } = useContext(LangContext);
  const [menu, setMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const openWhatsApp = useWhatsApp();

  const anchorClick = (hash) => (e) => {
    e.preventDefault();
    setMenu(false);
    if (location.pathname === "/") {
      if (window.__lenis) window.__lenis.scrollTo(hash, { offset: -72 });
      else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/" + hash);
    }
  };

  const navItems = [["home", "#home"], ["about", "#about"], ["tours", "#tours"], ["vedamrit", "#vedamrit"], ["trust", "#trust"], ["gallery", "#gallery"], ["contact", "#contact"]];

  return (
    <>
      <header className="site-header" data-testid="site-header">
        <Link to="/" className="brand" data-testid="brand-link" onClick={() => setMenu(false)}>
          <img src="/logo.svg" alt="Teerthaatan logo" className="brand-mark" />
          <span className="brand-word"><strong>Teerthaatan</strong><em>तीर्थाटन</em></span>
        </Link>
        <nav className="nav-links desktop-nav" data-testid="main-navigation">
          {navItems.map(([key, href]) => (
            <a key={key} href={"/" + href} onClick={anchorClick(href)} data-testid={`nav-${key}-link`}>{copy.nav[key]}</a>
          ))}
          <Link to="/kumbh" className="nav-kumbh" data-testid="nav-kumbh-link"><Tent size={14} /> {copy.nav.kumbh}</Link>
        </nav>
        <div className="header-actions">
          <div className="language-toggle" data-testid="language-toggle">
            <button className={lang === "hi" ? "active" : ""} onClick={() => changeLang("hi")} data-testid="language-hindi-button">हिन्दी</button>
            <span>|</span>
            <button className={lang === "en" ? "active" : ""} onClick={() => changeLang("en")} data-testid="language-english-button">English</button>
          </div>
          <button className="header-wa" onClick={() => openWhatsApp("general")} data-testid="header-whatsapp-button"><MessageCircle size={15} /><span>WhatsApp</span></button>
          <button className="menu-toggle" aria-label="Toggle menu" onClick={() => setMenu(!menu)} data-testid="mobile-menu-button">{menu ? <X /> : <Menu />}</button>
        </div>
      </header>
      <AnimatePresence>
        {menu && (
          <motion.nav className="mobile-nav" initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3, ease: EASE }} data-testid="mobile-navigation">
            {navItems.map(([key, href]) => (
              <a key={key} href={"/" + href} onClick={anchorClick(href)} data-testid={`mobile-nav-${key}-link`}>{copy.nav[key]}</a>
            ))}
            <Link to="/kumbh" onClick={() => setMenu(false)} data-testid="mobile-nav-kumbh-link" className="mobile-nav-kumbh">{copy.nav.kumbh}</Link>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
}

export function Footer() {
  const { copy } = useContext(LangContext);
  const location = useLocation();
  const navigate = useNavigate();
  const anchorClick = (hash) => (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      if (window.__lenis) window.__lenis.scrollTo(hash, { offset: -72 });
      else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    } else navigate("/" + hash);
  };
  const navItems = [["home", "#home"], ["about", "#about"], ["tours", "#tours"], ["vedamrit", "#vedamrit"], ["trust", "#trust"]];
  return (
    <footer className="site-footer">
      <div className="footer-main">
        <Link to="/" className="brand footer-brand">
          <img src="/logo.svg" alt="Teerthaatan logo" className="brand-mark" />
          <span className="brand-word"><strong>Teerthaatan</strong><em>तीर्थाटन</em></span>
        </Link>
        <p>{copy.footer.tagline}</p>
        <nav>{navItems.map(([key, href]) => <a key={key} href={"/" + href} onClick={anchorClick(href)} data-testid={`footer-${key}-link`}>{copy.nav[key]}</a>)}</nav>
        <div className="footer-kumbh">
          <span className="footer-kumbh-title">{copy.footer.kumbhLinks}</span>
          <Link to="/kumbh" data-testid="footer-kumbh-link">Kumbh Mela 2027–28</Link>
          <Link to="/kumbh/nashik">Nashik–Trimbakeshwar</Link>
          <Link to="/kumbh/ujjain">Ujjain</Link>
          <Link to="/kumbh/prayagraj">Prayagraj</Link>
          <Link to="/kumbh/haridwar">Haridwar</Link>
        </div>
        <div className="footer-blessing">{copy.footer.blessing}</div>
      </div>
      <div className="footer-bottom"><span>© Teerthaatan</span><span>Varanasi, Uttar Pradesh</span></div>
    </footer>
  );
}

export function FloatingWA() {
  const openWhatsApp = useWhatsApp();
  return (
    <motion.button className="floating-wa" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => openWhatsApp("general")} aria-label="Chat on WhatsApp" data-testid="floating-whatsapp-button">
      <MessageCircle size={22} /><span>WhatsApp</span>
    </motion.button>
  );
}
